/* eslint-disable formatjs/enforce-description */
import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { Button } from '@trello/nachos/button';
import { Dialog, useDialog } from '@trello/nachos/dialog';
import { AddMemberIcon } from '@trello/nachos/icons/add-member';
import { HelpIcon } from '@trello/nachos/icons/help';
import { useWorkspaceUserLimit } from '@trello/workspaces/user-limit';

import { LazyWorkspaceInviteModal } from 'app/src/components/WorkspaceInviteModal';
import { WorkspaceUserLimitUpsellTooltip } from 'app/src/components/WorkspaceUserLimitUpsellTooltip';
import { noop } from 'app/src/noop';

import styles from './InviteWorkspaceMembersButton.less';

interface InviteWorkspaceMembersButtonProps {
  workspaceId: string;
}

export const InviteWorkspaceMembersButton: FunctionComponent<
  InviteWorkspaceMembersButtonProps
> = ({ workspaceId }) => {
  const { isWorkspaceFree, isAtLimit, isOverLimit } = useWorkspaceUserLimit({
    workspaceId,
  });

  const { show, hide, dialogProps } = useDialog();

  const shouldShowUpgradePopover =
    isWorkspaceFree && (isAtLimit || isOverLimit);

  const onClick = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'inviteToWorkspaceButton',
      source: 'workspaceProfileScreen',
      containers: formatContainers({
        workspaceId,
      }),
    });

    show();
  }, [workspaceId, show]);

  if (shouldShowUpgradePopover) {
    return (
      <WorkspaceUserLimitUpsellTooltip workspaceId={workspaceId}>
        <Button
          appearance="default"
          iconBefore={<AddMemberIcon size="small" />}
          iconAfter={<HelpIcon size="small" />}
          // Turns out electron doesn't trigger mouse in/out events on disabled
          // elements, which breaks the tooltip trigger a bit.
          className="disabled"
          aria-disabled
          onClick={noop}
        >
          <FormattedMessage
            id="templates.org_detail.invite-team-members"
            defaultMessage="Invite Workspace members"
          />
        </Button>
      </WorkspaceUserLimitUpsellTooltip>
    );
  }

  return (
    <>
      <Button
        appearance="primary"
        iconBefore={<AddMemberIcon size="small" />}
        onClick={onClick}
        className={styles.button}
      >
        <FormattedMessage
          id="templates.org_detail.invite-team-members"
          defaultMessage="Invite Workspace members"
        />
      </Button>
      <Dialog alignment="center" {...dialogProps}>
        <LazyWorkspaceInviteModal workspaceId={workspaceId} onClose={hide} />
      </Dialog>
    </>
  );
};
